import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import VacationRequestForm from '../components/VacationRequestForm';
import SicknessForm from '../components/SicknessForm';
import { VacationCard, SicknessCard } from '../components/RequestCards';
import {
  apiListVacations, apiCreateVacation,
  apiListSickness, apiCreateSickness
} from '../api';
import { Menu, Bot, Plus, ClipboardList, Stethoscope, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const RequestsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTab = params.get('tab') === 'sickness' ? 'sickness' : 'vacation';

  const [tab, setTab] = useState(initialTab);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vacationRequests, setVacationRequests] = useState([]);
  const [sicknessProtocols, setSicknessProtocols] = useState([]);
  const [vacDialogOpen, setVacDialogOpen] = useState(false);
  const [sickDialogOpen, setSickDialogOpen] = useState(false);

  useEffect(() => {
    apiListVacations().then(setVacationRequests).catch(() => setVacationRequests([]));
    apiListSickness().then(setSicknessProtocols).catch(() => setSicknessProtocols([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setTab(initialTab); }, [initialTab]);

  const myVacations = useMemo(
    () => vacationRequests.filter(r => r.employeeEmail === user?.email).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [vacationRequests, user]
  );
  const mySickness = useMemo(
    () => sicknessProtocols.filter(r => r.employeeEmail === user?.email).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [sicknessProtocols, user]
  );

  const submitVacation = async (data) => {
    try {
      const saved = await apiCreateVacation(data);
      setVacationRequests(prev => [saved, ...prev]);
      setVacDialogOpen(false);
      toast.success(t('hr.requests.requestSubmitted'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.saveError'));
    }
  };

  const submitSickness = async (data) => {
    try {
      const saved = await apiCreateSickness(data);
      setSicknessProtocols(prev => [saved, ...prev]);
      setSickDialogOpen(false);
      toast.success(t('hr.requests.requestSubmitted'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.saveError'));
    }
  };

  const sidebarProps = {
    chats: [], activeChatId: null,
    onNewChat: () => navigate('/'),
    onSelectChat: (id) => navigate(`/chat/${id}`),
    onDeleteChat: () => {},
    isOpen: sidebarOpen,
    onClose: () => setSidebarOpen(false)
  };

  return (
    <div className="h-screen w-screen flex bg-white overflow-hidden">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar {...sidebarProps} />

      <main className="flex-1 flex flex-col min-w-0 bg-slate-50/30">
        <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-700 flex items-center justify-center">
                <Inbox className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">{t('hr.requests.title')}</div>
                <div className="text-xs text-slate-500">{t('sidebar.vacation')} · {t('sidebar.sickness')}</div>
              </div>
            </div>
          </div>
          <Button onClick={() => navigate('/')} variant="ghost" className="text-sm gap-2">
            <Bot className="w-4 h-4" /> {t('common.back')}
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto custom-scroll">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
            <Tabs value={tab} onValueChange={setTab}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <TabsList className="bg-white border border-slate-200">
                  <TabsTrigger value="vacation" className="gap-2">
                    <ClipboardList className="w-4 h-4" /> {t('hr.requests.vacation')}
                    {myVacations.length > 0 && <span className="ml-1 text-[10px] bg-slate-100 text-slate-700 px-1.5 rounded-full">{myVacations.length}</span>}
                  </TabsTrigger>
                  <TabsTrigger value="sickness" className="gap-2">
                    <Stethoscope className="w-4 h-4" /> {t('hr.requests.sickness')}
                    {mySickness.length > 0 && <span className="ml-1 text-[10px] bg-slate-100 text-slate-700 px-1.5 rounded-full">{mySickness.length}</span>}
                  </TabsTrigger>
                </TabsList>

                {tab === 'vacation' ? (
                  <Dialog open={vacDialogOpen} onOpenChange={setVacDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-700 hover:bg-blue-800 gap-2"><Plus className="w-4 h-4" /> {t('hr.requests.newVacationRequest')}</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{t('hr.requests.newVacationRequest')}</DialogTitle>
                        <DialogDescription>{t('hr.requests.submitRequest')}</DialogDescription>
                      </DialogHeader>
                      <VacationRequestForm onSubmit={submitVacation} onCancel={() => setVacDialogOpen(false)} />
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Dialog open={sickDialogOpen} onOpenChange={setSickDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-700 hover:bg-blue-800 gap-2"><Plus className="w-4 h-4" /> {t('hr.requests.newSicknessRequest')}</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{t('hr.requests.newSicknessRequest')}</DialogTitle>
                        <DialogDescription>{t('hr.requests.certificateNumber')}</DialogDescription>
                      </DialogHeader>
                      <SicknessForm onSubmit={submitSickness} onCancel={() => setSickDialogOpen(false)} />
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              <TabsContent value="vacation" className="space-y-3 mt-0">
                {myVacations.length === 0 ? (
                  <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
                    <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <div className="text-sm font-medium text-slate-700">{t('hr.requests.noRequests')}</div>
                  </div>
                ) : (
                  myVacations.map(r => <VacationCard key={r.id} item={r} />)
                )}
              </TabsContent>

              <TabsContent value="sickness" className="space-y-3 mt-0">
                {mySickness.length === 0 ? (
                  <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
                    <Stethoscope className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <div className="text-sm font-medium text-slate-700">{t('hr.requests.noRequests')}</div>
                  </div>
                ) : (
                  mySickness.map(r => <SicknessCard key={r.id} item={r} />)
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RequestsPage;
